import {
  Component,
  EnvironmentInjector,
  NgZone,
  OnDestroy,
  OnInit,
  runInInjectionContext,
  SecurityContext,
  inject,
  signal,
} from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { PtsPaymentTerminalService } from 'src/app/shared/services/payment-terminal.service';
import { FeedbackInfoComponent, SnackbarService } from 'ok-angular-shared/components';
import {
  ChargingStationLocationInfo,
  ErrorMessageType,
  PtsPaymentTerminal,
  ScreenUpdateCommand,
  UiElement,
  UiLogResponse,
} from '../../pts-payment-terminal-model';
import { Subscription, debounceTime, distinctUntilChanged, firstValueFrom, tap } from 'rxjs';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Firestore, doc, DocumentReference, DocumentData } from '@firebase/firestore';
import { LoadingSpinnerComponent } from '../../../../../core/loading-spinner/loading-spinner.component';
import { UiLogCardComponent } from '../terminal-ui-logs-viewer-dialog/ui-log-card/ui-log-card.component';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSuffix, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { FIRESTORE_INSTANCE } from 'src/app/shared/services/firestore-instance.token';
import { observeDocData } from 'src/app/shared/services/firestore-observables';

@Component({
  selector: 'adm-terminal-emulator',
  templateUrl: './terminal-emulator-dialog.component.html',
  styleUrl: './terminal-emulator-dialog.component.scss',
  imports: [
    LoadingSpinnerComponent,
    UiLogCardComponent,
    CustomDropDownArrowDirective,
    FeedbackInfoComponent,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatHint,
    MatInput,
    MatSelect,
    MatOption,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class TerminalEmulatorDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<TerminalEmulatorDialogComponent>>(MatDialogRef);
  data = inject<{
    paymentTerminal: PtsPaymentTerminal;
  }>(MAT_DIALOG_DATA);
  private ptsPaymentTerminalService = inject(PtsPaymentTerminalService);
  private snackbarService = inject(SnackbarService);
  private sanitizer = inject(DomSanitizer);
  private firestore = inject<Firestore>(FIRESTORE_INSTANCE);
  private ngZone = inject(NgZone);
  private injector = inject(EnvironmentInjector);
  private dialog = inject(MatDialog);

  public amounts: Amount[];
  public filteredAmounts: Amount[];
  public typeAheadModel: FormGroup;
  public amountForm: FormGroup;
  public qrCodeForm: FormGroup;
  public subscriptions: Subscription[] = [];

  paymentTerminal: PtsPaymentTerminal;
  latestUiLog = signal<UiLogResponse | null>(null);
  screenUpdateCommand: typeof ScreenUpdateCommand = ScreenUpdateCommand;
  errorMessageType: typeof ErrorMessageType = ErrorMessageType;
  loading = signal<boolean>(false);
  commandsEnabled = signal<boolean>(true);
  doCustomMessage = false;
  doQrCodeSend = false;
  doActivateCardReaderDropdown = false;
  customErrorMessageMaxLength = 65;
  terminalStateInFirestore = signal('IDLE');
  customErrorFormGroup: FormGroup = new FormGroup({
    customErrorMessage: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(this.customErrorMessageMaxLength)]),
  });
  get customErrorMessage() {
    return this.customErrorFormGroup.get('customErrorMessage')!;
  }
  get typeAheadQuery() {
    return this.typeAheadModel.controls['typeAheadQuery'];
  }
  get amount() {
    return this.amountForm.controls['amount'];
  }
  get urlForQrCode() {
    return this.qrCodeForm.controls['urlForQrCode'];
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
  async ngOnInit(): Promise<void> {
    this.paymentTerminal = this.data.paymentTerminal;
    await this.consolidateChargingStationLocationInfo();
    this.commandsEnabled.set(this.data.paymentTerminal.online);
    this.amounts = this.getAmounts();
    this.filteredAmounts = this.amounts;
    this.typeAheadModel = this.initTypeAhead();
    this.amountForm = new FormGroup({
      amount: new FormControl<number>(0),
    });
    this.qrCodeForm = new FormGroup({
      urlForQrCode: new FormControl<string | null>(null),
    });
    this.payterTerminalCollectionSubscription(this.data.paymentTerminal.serialNumber);
  }

  async consolidateChargingStationLocationInfo() {
    if (!this.paymentTerminal.chargingStationLocationInfo) {
      const chargingStationLocationInfos = (await firstValueFrom(
        this.ptsPaymentTerminalService.getChargingStationInfoForTerminals([this.paymentTerminal.serialNumber])
      )) as ChargingStationLocationInfo[];
      this.paymentTerminal.chargingStationLocationInfo = chargingStationLocationInfos[0];
    }
  }
  public resetCustomInputFields() {
    this.doCustomMessage = false;
    this.doQrCodeSend = false;
    this.doActivateCardReaderDropdown = false;
  }

  public resetAmount() {
    this.amount.patchValue(null);
    this.typeAheadQuery.setValue('');
  }

  public async payterTerminalCollectionSubscription(serialNumber: string) {
    await runInInjectionContext(this.injector, async () => {
      const payterTerminalDocRef = doc(this.firestore, `OK/Emsp/PayterTerminals/${serialNumber}`) as DocumentReference<DocumentData>;

      const subscription = observeDocData(payterTerminalDocRef, this.ngZone).subscribe({
        next: terminal => {
          if (terminal) {
            this.latestUiLog.set(null);
            this.loading.set(true);
            this.terminalStateInFirestore.set(terminal['State']);

            let parsedUiLog: UiElement | null = null;
            if (terminal['JsonUi']) {
              try {
                parsedUiLog = JSON.parse(terminal['JsonUi']) as UiElement;
              } catch {
                parsedUiLog = null;
              }
            }

            const latestUiLog: UiLogResponse = {
              id: terminal['Id'] ?? null,
              type: terminal['Type'] ?? null,
              callbackUrl: terminal['CallbackUrl'] ?? null,
              created: terminal['Created'] ?? null,
              jsonUi: terminal['JsonUi'],
              serialNumber,
              state: terminal['State'],
              updated: terminal['Updated']?.toDate(),
              uiElement: parsedUiLog,
              createdBy: '',
              locations: [],
            };

            this.loading.set(false);
            this.latestUiLog.set(latestUiLog);
          } else {
            this.latestUiLog.set(null);
          }
        },
      });

      this.subscriptions.push(subscription);
    });
  }
  async sendCommand(
    command: ScreenUpdateCommand,
    errorMessageType?: ErrorMessageType,
    customErrorMessage: string | null = null,
    urlForQrCode: string | null = null
  ): Promise<void> {
    this.loading.set(true);
    if (this.terminalStateInFirestore() === 'READING') {
      this.ptsPaymentTerminalService
        .sendCommand(this.paymentTerminal.serialNumber, ScreenUpdateCommand.StopCardRead)
        .pipe(
          tap(() => {
            this.commandsEnabled.set(false);
            setTimeout(() => {
              this.commandsEnabled.set(true);
            }, 5000);
          })
        )
        .subscribe({
          next: () => {
            this.snackbarService.info(
              'Terminal er i READING state. Stopper kortlæsning. Prøv igen om et øjeblik når knapper bliver klikbare igen.'
            );
            this.loading.set(false);
          },
          error: error => {
            this.snackbarService.error(`Kunne ikke sende kommando: ${error?.error.message}`);
            this.loading.set(false);
          },
        });
    } else if (command === ScreenUpdateCommand.SendQrCode) {
      this.latestUiLog.set(null);
      this.ptsPaymentTerminalService
        .sendCommand(this.paymentTerminal.serialNumber, command, errorMessageType ?? undefined, null, null, urlForQrCode)
        .subscribe({
          error: error => {
            this.snackbarService.error(`Kunne ikke sende kommando: ${error?.error.message}`);
            this.loading.set(false);
          },
        });
    } else {
      this.latestUiLog.set(null);
      this.ptsPaymentTerminalService
        .sendCommand(this.paymentTerminal.serialNumber, command, errorMessageType ?? undefined, customErrorMessage, this.amount.value)
        .subscribe({
          error: error => {
            this.snackbarService.error(`Kunne ikke sende kommando: ${error?.error.message}`);
            this.loading.set(false);
          },
        });
    }
  }

  close(): void {
    this.dialogRef.close();
  }
  public initTypeAhead() {
    const typeAheadQuery = '';
    const typeAheadFormGroup = new FormGroup({
      typeAheadQuery: new FormControl(typeAheadQuery),
    });

    typeAheadFormGroup.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe(() => {
        if (typeof this.typeAheadQuery.value === 'object') {
          const amountObject = this.typeAheadQuery.value as Amount;
          const amountInOere = parseInt((amountObject.amount * 100).toFixed());
          this.amountForm.controls['amount'].setValue(amountInOere);
        } else {
          if (this.typeAheadQuery.value) {
            const amountSearch = this.typeAheadQuery.value;
            this.filteredAmounts = this.amounts.filter(o => o.name.toLowerCase().indexOf(amountSearch?.toLowerCase()) > -1);
            try {
              this.amountForm.controls['amount'].setValue(parseInt(amountSearch));
            } catch (error) {
              this.snackbarService.error('Beløbet skal være et tal');
            }
          } else {
            this.filteredAmounts = this.amounts;
          }
        }
      });

    return typeAheadFormGroup;
  }
  public displayFn(amount: Amount | null): string {
    return amount && amount.name ? `${amount.amount} : ${amount.name}` : '';
  }

  private getAmounts(): Amount[] {
    return [
      { name: 'Approval', amount: 4.1 },
      { name: 'Decline', amount: 5.0 },
      { name: 'Declined Invalid issuer data', amount: 5.01 },
      { name: 'Declined Invalid issuer data', amount: 5.02 },
      { name: 'Declined Do not honor', amount: 5.03 },
      { name: 'Timeout (response after 90 seconds)', amount: 7.0 },
      { name: 'Network error after sending', amount: 7.01 },
      { name: 'Network error before sending', amount: 7.02 },
      { name: 'Partial Approval (for 10.00)', amount: 10.27 },
      { name: 'Withdrawal limit amount exceeded', amount: 14.29 },
      { name: 'Withdrawal frequency exceeded', amount: 14.3 },
      { name: 'PIN Required', amount: 14.31 },
      { name: 'Partial Approval (for 40.00)', amount: 40.27 },
      { name: 'Decline - Unable to connect to acquirer', amount: 48.03 },
      { name: 'Decline - Connection to acquirer timed out', amount: 48.04 },
      { name: 'Decline - Acquirer rejected request', amount: 48.05 },
      { name: 'Decline - Acquirer response invalid', amount: 48.06 },
      { name: 'Decline - PIN invalid, After the third retry Pin Approved', amount: 49.01 },
    ];
  }

  handleTerminalEmulator(paymentTerminal: PtsPaymentTerminal) {
    this.dialog.open(TerminalEmulatorDialogComponent, {
      data: {
        paymentTerminal: paymentTerminal,
      },
      width: '1024px',
      minHeight: '650px',
    });
  }
}

export interface Amount {
  name: string;
  amount: number;
}
